import { useEffect, useRef } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import useVirtualKeyboardVisible from '@/hooks/use-virtual-keyboard-visible';

type OriginalStyle = {
  maxHeight: CSSStyleDeclaration['maxHeight'];
  minHeight: CSSStyleDeclaration['minHeight'];
};

type Props = {
  inputTargetSelector?: string;
  layoutTargetSelector?: string;
};

export default function useAdjustLayout({ inputTargetSelector, layoutTargetSelector = '#page-layout' }: Props = {}) {
  const { isKeyboardVisible, viewportHeight } = useVirtualKeyboardVisible(inputTargetSelector);
  const originalStyleRef = useRef<OriginalStyle>();

  const handleScroll = () => {
    const target = document.querySelector<HTMLDivElement>(layoutTargetSelector);
    debouncedHandleScroll.cancel();
    if (isKeyboardVisible) {
      target?.scrollIntoView({ block: 'start', behavior: 'instant' });
      window.scrollBy({
        left: 0,
        top: -(viewportHeight ?? 0),
        behavior: 'instant',
      });
    }
  };

  const debouncedHandleScroll = useDebouncedCallback(handleScroll, 100);

  useEffect(() => {
    const target = document.querySelector<HTMLDivElement>(layoutTargetSelector);

    window.visualViewport?.addEventListener('scroll', debouncedHandleScroll);

    if (!target || !isKeyboardVisible) {
      return;
    }

    originalStyleRef.current = {
      maxHeight: target.style.maxHeight,
      minHeight: target.style.minHeight,
    };

    target.style.maxHeight = `${viewportHeight}px`;
    target.style.minHeight = `${viewportHeight}px`;

    return () => {
      window.visualViewport?.removeEventListener('scroll', debouncedHandleScroll);

      if (originalStyleRef.current) {
        target.style.maxHeight = originalStyleRef.current.maxHeight;
        target.style.minHeight = originalStyleRef.current.minHeight;
      }
    };
  }, [debouncedHandleScroll, isKeyboardVisible, viewportHeight]);

  return { isKeyboardVisible } as const;
}
